import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { createOrg, createCategory, putOrgPublicKeySig, deleteOrg, getCurrentUserId } from '../api/client';
import Button from '../components/Button';
import { generateFamilyKeyPair } from '../crypto/familyKeys';
import { encryptCategoryName } from '../crypto/categoryName';
import { useFamilyKeys } from '../context/FamilyKeysContext';
import { bytesToBase64 } from '../crypto/crypto';
import { useAccountKey } from '../context/AccountKeyContext';
import { encryptFamilyHandoff, entitlementErrorOr } from '@heybesafe/shared';
import { useEntitlement } from '../hooks/useEntitlement';
import { ProModal } from '../components/ProModal';
import { AddSeatsCTA } from '../components/AddSeatsCTA';

export default function OrgCreatePage() {
  const navigate = useNavigate();
  const { canCreateFamily, snapshot, refresh: refreshEntitlement } = useEntitlement();
  const { refreshOrgs, signWithIdentityKey, identityPublicKey } = useSafe();
  const { addKey } = useFamilyKeys();
  const { publicKey: accountPublicKey, recoveryFailed: accountKeyRecoveryFailed } = useAccountKey();

  const [name, setName]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    setLoading(true);
    try {
      if (!accountPublicKey) {
        // Distinguish the brief still-loading race (retryable) from a persistent
        // recovery failure (a stale local wrap that only a full sign-in fixes —
        // see AccountSetupBanner). "Try again in a moment" is misleading for the
        // latter; point the user at signing in again instead.
        setError(accountKeyRecoveryFailed
          ? 'Your saved session is out of date — please sign in again to finish account setup, then try again.'
          : 'Finishing account setup — try again in a moment.');
        return;
      }

      // PKA hardening (S1): the founder's identity key MUST be loaded before we
      // create a family, so the new family's public key is signed (below) and no
      // unsigned family is ever created. Once signature enforcement is universal
      // (S3), an unsigned family would be unusable — so block here (retryable)
      // rather than create one. Mirrors the account-key gate above.
      if (!identityPublicKey) {
        setError(accountKeyRecoveryFailed
          ? 'Your saved session is out of date — please sign in again to finish account setup, then try again.'
          : 'Finishing account setup — try again in a moment.');
        return;
      }

      // Capability check: X25519 needs Safari 16.4+, Chrome 110+, Firefox 130+.
      // Surface a clear error instead of letting subtle.generateKey throw a
      // cryptic "Unsupported algorithm" message.
      let keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
      try {
        keyPair = await generateFamilyKeyPair();
      } catch {
        setError('Your browser does not support the encryption required for Families. Update to a recent Chrome, Firefox, or Safari and try again.');
        return;
      }
      const { publicKey, privateKey } = keyPair;

      const familyKeyWrap = await encryptFamilyHandoff(privateKey, accountPublicKey);

      const publicKeyB64Url = bytesToBase64(publicKey)
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

      const result = await createOrg({
        name: name.trim(),
        publicKey: publicKeyB64Url,
        familyKeyWrap,
      });
      if (!result.ok) {
        // The server sends entitlement denials (e.g. TRIAL_FAMILY_LIMIT) with
        // the raw code as `error`. Map known entitlement codes to friendly copy
        // so the user never sees a bare constant; other errors already carry a
        // human `error` string from the handler, so fall back to that.
        setError(entitlementErrorOr(result.code, result.error));
        return;
      }

      const orgId = result.data.org.id;

      // PKA: sign the family public key with our identity key (bound to the
      // orgId, which the server assigns) and upload it, so members can
      // authenticate this family key (defeats a malicious server substituting
      // it). The identity key is guaranteed loaded (precondition checked above),
      // so the signature is always produced. The upload is write-once and has no
      // load-time backfill (retired once coverage hit 100%), and PKA enforcement
      // is universal — a family with no stored signature is rejected on every
      // client. So the upload MUST land for the family to be usable: retry once
      // for a transient blip, and if it still fails, roll the family back and
      // hard-fail rather than strand an unusable, signature-less family.
      const founderUserId = getCurrentUserId();
      const sig = founderUserId
        ? signWithIdentityKey({
            key: publicKeyB64Url,
            keyType: 'family-x25519',
            subject: orgId,
            context: orgId,
            notBefore: new Date().toISOString(),
            signerId: founderUserId,
          })
        : null;

      const uploadSig = async () => {
        try { return (await putOrgPublicKeySig(orgId, sig!)).ok; }
        catch { return false; }
      };
      // sig is guaranteed by the identity-key precondition above; retry the
      // upload once before giving up.
      let sigStored = sig ? await uploadSig() : false;
      if (sig && !sigStored) sigStored = await uploadSig();

      if (!sigStored) {
        console.error('Family public-key signature could not be stored after a retry; rolling back the new family');
        // Roll back the just-created (unusable, unsigned) family so the user can
        // cleanly retry, then hard-fail with a try-later message.
        await deleteOrg(orgId).catch((e) => {
          console.error('Failed to roll back the unsigned family; it may be left orphaned', e);
        });
        setError('We couldn’t finish securing your new family, so it wasn’t created. Please try creating it again in a little while.');
        return;
      }

      // Signature is stored — the family is usable. Cache the just-generated
      // privkey so subsequent screens have it without a re-fetch.
      addKey(orgId, privateKey);

      // Seed a default "General" category so the family has a base topic before
      // any configuration (nav-decisions: categories are the Binder's shell).
      // The name is encrypted client-side to the family key, exactly like an
      // admin-created category. Best-effort — if it fails, the family simply
      // starts with no categories and the admin can add them in Family Settings.
      try {
        const blob = await encryptCategoryName('General', undefined, publicKey);
        await createCategory(result.data.org.id, { ...blob, order: 0 });
      } catch (e) {
        console.warn('Could not seed the default "General" category', e);
      }

      // Creating a family changes entitlement: the server links the caller's
      // unused active sub to the new family (claimUnusedSub) and recomputes their
      // snapshot to 'active-subscriber'. Refresh the client snapshot so the new
      // family's Manage tab doesn't gate invite/etc. on the stale pre-create state
      // (otherwise a paying owner sees a "Subscribe to invite members" ProModal on
      // their brand-new family until the next reload/login). Best-effort — a
      // refresh hiccup must never block landing on the new family.
      try { await refreshEntitlement(); } catch { /* best-effort */ }
      await refreshOrgs();
      navigate(`/orgs/${result.data.org.id}`);
    } finally {
      setLoading(false);
    }
  }

  if (!canCreateFamily) {
    if (snapshot?.state === 'active-subscriber') {
      return (
        <div className="max-w-md mx-auto px-6 py-6 text-center space-y-4">
          <h1 className="text-xl font-semibold text-hbs-text">You're at your subscription's seat limit</h1>
          <p className="text-sm text-hbs-text-mute">
            Add a 5-seat pack to your subscription, then come back to create another family.
          </p>
          <AddSeatsCTA label="Add seats" />
          <button
            id="org-create-seats-back-button"
            onClick={() => navigate('/orgs', { replace: true })}
            className="text-xs text-hbs-text-mute hover:text-hbs-text"
          >
            Back to families
          </button>
        </div>
      );
    }
    return <ProModal feature="create-family" onClose={() => navigate('/orgs', { replace: true })} />;
  }

  return (
    <div className="max-w-md mx-auto px-6 py-6">
      <div className="flex items-center gap-2 mb-6">
        <button
          id="org-create-back-button"
          onClick={() => navigate('/orgs')}
          className="p-1 rounded text-hbs-text-mute hover:text-hbs-text transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-semibold text-hbs-text">Create Family</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <label htmlFor="org-name" className="block text-xs font-medium text-hbs-text-mute mb-1">
            Family Name
          </label>
          <input
            id="org-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Acme Corp"
            autoFocus
            required
            className="w-full bg-hbs-surface border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text placeholder-hbs-text-mute focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
          />
        </div>

        {error && <p className="text-red-400 text-sm bg-red-400/10 rounded-hbs-md p-3">{error}</p>}

        <div className="flex gap-2">
          <Button id="org-create-submit-button" type="submit" loading={loading} fullWidth>Create</Button>
          <Button id="org-create-cancel-button" variant="secondary" onClick={() => navigate('/orgs')} fullWidth>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
